import { useState, useEffect } from "react";
import { Check, Search, MapPin, X, Loader2 } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { geocodeCity } from "@/lib/jobbkartaApi";

export default function CitySelector({ presets, selected, onToggle, triggerClassName }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  // debounced geocode search for any Swedish city
  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const t = setTimeout(async () => {
      try {
        const data = await geocodeCity(term);
        setResults(data);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [q]);

  const isActive = (city) => selected.some((s) => s.id === city.id);

  const options = q.trim().length >= 2 ? results : presets;

  const label =
    selected.length === 0
      ? "Välj stad"
      : selected.length === 1
      ? selected[0].name
      : `${selected.length} städer`;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          data-testid="city-selector-trigger"
          className={`gap-2 rounded-full border-slate-300 bg-white font-medium ${triggerClassName || ""}`}
        >
          <MapPin className="h-4 w-4 text-emerald-600" />
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="z-[1300] w-80 p-0" data-testid="city-selector-popover">
        <div className="flex items-center gap-2 border-b border-slate-100 px-3 py-2">
          <Search className="h-4 w-4 text-slate-400" />
          <input
            data-testid="city-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Sök vilken stad som helst i Sverige…"
            className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
          />
          {loading ? (
            <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
          ) : q ? (
            <button onClick={() => setQ("")} aria-label="Rensa">
              <X className="h-4 w-4 text-slate-400" />
            </button>
          ) : null}
        </div>

        {q.trim().length < 2 && (
          <p className="px-3 pt-2 text-[11px] font-semibold uppercase tracking-wider text-slate-400">
            Populära städer
          </p>
        )}

        <div className="jk-scroll max-h-64 overflow-y-auto p-1">
          {!loading && options.length === 0 && (
            <p className="px-3 py-4 text-sm text-slate-400">
              {q.trim().length >= 2 ? "Ingen stad matchar din sökning." : "Inga städer."}
            </p>
          )}
          {options.map((c) => {
            const active = isActive(c);
            return (
              <button
                key={c.id}
                data-testid={`city-option-${c.id}`}
                onClick={() => onToggle(c)}
                className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-left text-sm transition-colors ${
                  active ? "bg-emerald-50 text-emerald-800" : "hover:bg-slate-50 text-slate-700"
                }`}
              >
                <span className="flex min-w-0 items-center gap-2">
                  <MapPin className={`h-4 w-4 shrink-0 ${active ? "text-emerald-600" : "text-slate-400"}`} />
                  <span className="truncate">
                    {c.name}
                    {c.region ? <span className="text-slate-400"> · {c.region}</span> : null}
                  </span>
                </span>
                {active && <Check className="h-4 w-4 shrink-0 text-emerald-600" />}
              </button>
            );
          })}
        </div>

        {selected.length > 0 && (
          <div className="flex flex-wrap gap-1.5 border-t border-slate-100 p-2">
            {selected.map((c) => (
              <Badge
                key={c.id}
                data-testid={`city-badge-${c.id}`}
                variant="secondary"
                className="cursor-pointer gap-1 bg-emerald-100 text-emerald-800 hover:bg-emerald-200"
                onClick={() => onToggle(c)}
              >
                {c.name}
                <X className="h-3 w-3" />
              </Badge>
            ))}
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
